import { NamespaceBoundary } from '@/lib/i18n/NamespaceBoundary'
import { useTranslation } from '@/lib/i18n/react'
import { localizedErrorMessage } from '@/lib/i18n/errors'
import { WrongPortalError } from '@/features/auth/login-portal'
import { LanguageSelector } from '@/features/locale/components/LanguageSelector'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useAuthStore } from '@/store/auth-store'
import { openSession } from '@/features/auth/session-navigation'
import { sameOriginPath } from '@/lib/utils/safe-url'
import { useLogin } from '@/features/auth/hooks/useLogin'
import { WorkspaceChooser } from '@/features/auth/components/WorkspaceChooser'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { BlazeLogo } from '@/components/branding/BlazeLogo'
import { useRef, useState } from 'react'
import {
  MessagesSquare,
  Sparkles,
  Users,
  KanbanSquare,
  ShieldCheck,
} from 'lucide-react'
import type { MembershipChoiceDto } from '@blazeteams/sdk'
import '@/styles/blaze-kit/login.css'

/**
 * `?redirect=` — a dónde volver después de autenticarse.
 *
 * `_auth.tsx` lo viene emitiendo (`redirect: location.href`) desde siempre,
 * pero NADIE lo leía: al loguearse el usuario aterrizaba en el Inbox y el
 * destino se perdía. Con un deep link al dashboard era una molestia; con
 * `/oauth/consent` es una rotura, porque ahí el destino LLEVA los parámetros
 * de OAuth (client_id, state, code_challenge…) que no existen en ningún otro
 * lado y sin los cuales el flujo de consentimiento no puede continuar.
 *
 * Se navega con `href` + recarga de documento a propósito: preserva el query
 * string tal cual, sin que el router lo re-serialice — y el contrato de OAuth
 * exige que esos parámetros vuelvan byte por byte.
 */
export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  beforeLoad: ({ search }) => {
    const token = useAuthStore.getState().token
    if (token) {
      // `sameOriginPath` filtra el open redirect: honrar un destino del query
      // string sin filtrar mandaría al usuario a un clon del login justo
      // después de autenticarse.
      const destino = sameOriginPath(search.redirect)
      // An already authenticated visitor can keep their current document.
      // Successful login below always starts a fresh document/session.
      throw destino
        ? redirect({ href: destino, reloadDocument: destino.includes('?') })
        : redirect({ to: '/' })
    }
  },
  component: LoginPage,
})

interface ChooseTenantState {
  intentToken: string
  memberships: MembershipChoiceDto[]
}

/**
 * Pitch de valor del panel izquierdo. Copys cortos: el login es la primera
 * pantalla que ve un agente invitado — le dice QUÉ es BlazeTeams antes de
 * pedirle credenciales.
 */
const FEATURES = [
  {
    icon: MessagesSquare,
    title: 'login.featureInbox',
    desc: 'login.featureInboxHelp',
  },
  {
    icon: Sparkles,
    title: 'login.featureAI',
    desc: 'login.featureAIHelp',
  },
  {
    icon: Users,
    title: 'login.featureTeam',
    desc: 'login.featureTeamHelp',
  },
  {
    icon: KanbanSquare,
    title: 'login.featureSales',
    desc: 'login.featureSalesHelp',
  },
] as const

// eslint-disable-next-line react-refresh/only-export-components
function LoginPage() {
  return <NamespaceBoundary namespace="auth"><LoginContent /></NamespaceBoundary>
}

// eslint-disable-next-line react-refresh/only-export-components
function LoginContent() {
  const { t } = useTranslation('auth')
  const { redirect: returnTo } = Route.useSearch()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  // When backend returns needs_tenant_choice we hold the intent_token +
  // the memberships in memory only (not localStorage — the intent_token
  // is short-lived (5 min) and a leaked one is more dangerous than a
  // forced re-login on refresh).
  const [chooseTenant, setChooseTenant] = useState<ChooseTenantState | null>(null)
  const [expiredChoice, setExpiredChoice] = useState(false)
  const login = useLogin()
  const submitting = useRef(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting.current) return
    submitting.current = true
    setExpiredChoice(false)
    login.mutate(
      { email, password },
      {
        onSettled: () => { submitting.current = false },
        onSuccess: (result) => {
          if (result.kind === 'choose_tenant') {
            setChooseTenant({
              intentToken: result.intentToken,
              memberships: result.memberships,
            })
            return
          }
          // Single-tenant flow finished by the hook — navigate away.
          const role = result.user.role ?? 'agent'
          const isAdmin = role === 'admin' || role === 'super_admin'
          if (isAdmin && result.user.onboarding_completed === false) {
            openSession('/onboarding')
            return
          }
          openSession(returnTo)
        },
      },
    )
  }

  // Multi-tenant chooser screen. Replaces the credentials form once
  // the backend asks the user to pick a workspace.
  if (chooseTenant) {
    return (
      <WorkspaceChooser
        intentToken={chooseTenant.intentToken}
        memberships={chooseTenant.memberships}
        onCancel={(reason) => { setChooseTenant(null); setExpiredChoice(reason === 'expired') }}
        returnTo={returnTo}
      />
    )
  }

  return (
    <div className="login-shell flex">
      {/* ── Panel de marca (oculto en mobile) ─────────────────────────── */}
      <aside className="login-brand-panel relative hidden w-[46%] max-w-2xl flex-col justify-between overflow-hidden p-12 text-white lg:flex">
        <div aria-hidden className="login-brand-grid" />
        <div aria-hidden className="login-brand-glow login-brand-glow--orange" />
        <div aria-hidden className="login-brand-glow login-brand-glow--pink" />
        <div aria-hidden className="login-brand-glow login-brand-glow--blue" />

        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <BlazeLogo size={62} className="login-brand-logo" />
            <div>
              <span className="block font-display text-xl font-extrabold tracking-tight">BlazeTeams</span>
              <span className="mt-1 block text-[9px] font-extrabold tracking-[.18em] text-white/45">{t('brand.by')}</span>
            </div>
          </div>

          <span className="login-brand-kicker">{t('login.kicker')}</span>
          <h1 className="mt-5 max-w-md font-display text-4xl font-extrabold leading-tight">
            {t('login.headlineOne')}
            <br />
            {t('login.headlineTwo')}
          </h1>
          <p className="text-white/85 text-sm mt-4 max-w-md leading-relaxed">
            {t('login.pitch')}
          </p>

          <ul className="mt-10 flex flex-col gap-5 max-w-md">
            {FEATURES.map((f) => (
              <li key={f.title} className="flex items-start gap-4">
                <span className="login-feature-icon">
                  <f.icon size={19} strokeWidth={2.2} />
                </span>
                <span>
                  <span className="block font-bold text-[15px]">{t(f.title)}</span>
                  <span className="block text-white/80 text-[13px] leading-relaxed mt-0.5">
                    {t(f.desc)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative z-10 flex items-center gap-2 text-xs text-white/60">
          <ShieldCheck size={14} />
          {t('login.security')}
        </p>
      </aside>

      {/* ── Formulario ────────────────────────────────────────────────── */}
      <main className="login-form-panel relative flex flex-1 flex-col items-center justify-center overflow-hidden p-6">
        <section className="login-form-card relative flex flex-col gap-7">
          <LanguageSelector />
          {/* Branding compacto para mobile (el aside está oculto) */}
          <div className="flex items-center justify-center gap-3 lg:hidden">
            <BlazeLogo size={58} className="login-brand-logo" />
            <div>
              <p className="font-display text-lg font-extrabold tracking-tight text-fg-1">BlazeTeams</p>
              <p className="text-[9px] font-extrabold tracking-[.16em] text-fg-3">{t('brand.by')}</p>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="login-form-eyebrow">{t('login.secureAccess')}</span>
            <h2 className="font-display text-2xl font-extrabold text-fg-1">{t('login.welcome')}</h2>
            <p className="text-sm text-fg-3">{t('login.instructions')}</p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <Input
              label={t('login.email')}
              dir="ltr"
              autoComplete="username"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              required
            />
            <Input
              label={t('login.password')}
              autoComplete="current-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />

            <a href="/forgot-password" className="text-end text-sm font-medium text-blaze-orange">
              {t('login.forgot')}
            </a>

            {expiredChoice && <p role="alert" className="text-xs text-red-500 font-medium">{t('workspace.expired')}</p>}
            {login.error && (
              <div role="alert" className="space-y-2 text-xs font-medium">
                <p className="text-red-500">{localizedErrorMessage(login.error, 'auth', 'errors.loginFailed')}</p>
                {login.error instanceof WrongPortalError && login.error.redirectTo && (
                  <a href={login.error.redirectTo} rel="noreferrer" className="flex min-h-11 flex-col justify-center gap-1 rounded-lg border border-line-soft px-3 py-2 text-blaze-orange">
                    <span>{t('login.openPortal')}</span>
                    <bdi dir="ltr" className="break-all text-fg-3">{login.error.redirectTo}</bdi>
                  </a>
                )}
              </div>
            )}

            <Button
              type="submit"
              variant="gradient"
              size="lg"
              className="login-submit mt-2 w-full"
              isLoading={login.isPending}
            >
              {t('login.submit')}
            </Button>
          </form>

          <p className="text-xs text-fg-3 text-center">
            {t('login.noAccount')}{' '}
            <span className="text-blaze-orange font-bold cursor-pointer">
              {t('login.contactAdmin')}
            </span>
          </p>
          <p className="login-security-note">
            <ShieldCheck size={12} /> {t('login.sessionSecurity')}
          </p>
        </section>
      </main>
    </div>
  )
}
